/**
 * Util functions to help make calls to both Mixpanel and Segment.
 */

import {
  MixpanelHookMethods,
  MIXPANEL_SUPER_PROPERTIES,
} from "../hooks/use-mixpanel";
import { SegmentHookMethods } from "../hooks";

/**
 * CARD_SELECTED
 *  - Tracks an event when a user clicks a specific entity card in the community.
 * PAGE_VIEW
 *  - Event that records a page view for the user.
 * LIKE
 *  - Tracks when a user clicks on a "Like" icon for a specific entity card.
 * SHARE
 *  - Tracks the share icon clicked by the user in the ShareBar component.
 * STORY_SHARED
 *  - Fired when a user successfully submits a story. The legacy app called this
 *    event "Story Shared" so we keep the same string here to preserve historical reports.
 */
export enum InspireAnalyticEvents {
  CARD_SELECTED = "Card Selected",
  LIKE = "Like",
  PAGE_VIEW = "Page View",
  SHARE = "share",
  RSVP_START = "RSVP - Start",
  CTA_CLICK = "CTA Click",
  THANK_YOU_BUTTON_CLICK = "Thank You Button Click",
  INVITE_CLICK = "Invite-Flow Click",
  INVITE_STEP_CHANGE = "Invite-Flow Step Change",
  THANK_YOU_CLICK = "ThankYou-Flow Click",
  THANK_YOU_STEP_CHANGE = "ThankYou-Flow Step Change",
  RSVP_SUBMIT = "RSVP - Submit",
  RSVP_COMPLETE = "RSVP - Complete",
  RSVP_CANCEL = "RSVP - Cancel",
  RSVP_QUESTION_INTERACTION = "RSVP - Question Interaction",
  DONATION_AMOUNT_CLICK = "Donation Amount Click",
  DONATION_COMPLETE = "Donation Complete",
  DONATE_NOW_CLICK = "Donate Now Click",
  STORY_SHARED = "Story Shared",
  COMMENT = "Comment",
  LAST_LOGIN = "Last Login",
}

/**
 * Get the "Page Type" property to send to Mixpanel/Segment based on the Next.js route
 */
const getPageType = (pageRoute: string) => {
  switch (pageRoute) {
    case "/community/[communitySlug]/communities":
      return "Community Sub-Communities Page";
    case "/community/[communitySlug]/detail":
      return "Community Detail Page";
    case "/community/[communitySlug]/events":
      return "Community Events Page";
    case "/community/[communitySlug]/stories":
      return "Community Stories Page";
    case "/events/[eventId]/detail":
      return "Event Detail Page";
    case "/events/[eventId]/edit":
      return "Event Edit Page";
    case "/events/[eventId]/donate":
      return "Donate Page";
    case "/events/[eventId]/share":
      return "Share Story Page";
    case "/stories/[storyId]":
      return "Story Detail Page";
    case "/actions/invites/event/[eventId]":
      return "Event Invite Flow";
    case "/events/[eventId]/thankyou":
      return "Event Thank You Flow";
    case "/events/[eventId]/rsvp":
      return "RSVP Page";
    default:
      return "";
  }
};

export const InspireAnalyticsHelper = {
  getPageType,
};

/**
 * Make Identify calls using the Mixpanel/Segment APIs.
 */
const identify = ({
  id,
  mixpanel,
  segment,
}: {
  id: string;
  mixpanel?: MixpanelHookMethods;
  segment?: SegmentHookMethods;
}) => {
  if (mixpanel) {
    mixpanel.identify(id);
  }

  if (segment) {
    segment.identify({ userId: id });
  }

  return;
};

/**
 * Make Track calls using the Mixpanel/Segment APIs.
 */
const track = ({
  eventName,
  properties,
  mixpanel,
  segment,
}: {
  eventName: string;
  properties: Record<string, any>;
  mixpanel?: MixpanelHookMethods;
  segment?: SegmentHookMethods;
}) => {
  let mixpanelSuperProperties = {};

  // Slight delay so all libraries can initialize in time. (for example when user authenticates via hash so mixpanel/segment identify them in time)
  setTimeout(() => {
    if (mixpanel) {
      mixpanel.track(eventName, properties);

      // Get all mixpanel super properties from the current mixpanel instance so we can also send them to segment (or any other library).
      mixpanelSuperProperties = mixpanel.getSuperProperties(
        MIXPANEL_SUPER_PROPERTIES
      );
    }

    if (segment) {
      segment.track({
        action: "",
        label: eventName,
        properties: {
          ...mixpanelSuperProperties,
          ...properties,
        },
      });
    }
  }, 2000);

  return;
};

/**
 * Update properties on the current user profile in Mixpanel.
 *
 * In legacy Dreambig these were direct `mixpanel.people.set` calls scattered across
 * controllers. Centralizing them here keeps the firing convention (no-op when
 * mixpanel is not configured) consistent.
 */
const setPeopleProperties = ({
  properties,
  mixpanel,
}: {
  properties: Record<string, any>;
  mixpanel?: MixpanelHookMethods;
}) => {
  if (mixpanel) {
    mixpanel.peopleSet(properties);
  }
};

/**
 * Set a property on the user profile only the first time the property is set.
 *
 * Mirrors the legacy `people.set_once` calls (e.g. `First Login TS`).
 */
const setPeoplePropertyOnce = ({
  properties,
  mixpanel,
}: {
  properties: Record<string, any>;
  mixpanel?: MixpanelHookMethods;
}) => {
  if (mixpanel) {
    mixpanel.peopleSetOnce(properties);
  }
};

/**
 * Increment a numeric property on the current user profile.
 *
 * Used for the legacy `# of *` counter family and the `zzBMVP1: *` counter
 * family powering "Total Donations Count", "# of Comments", etc. dashboards.
 */
const incrementPeopleProperty = ({
  property,
  value = 1,
  mixpanel,
}: {
  property: string;
  value?: number;
  mixpanel?: MixpanelHookMethods;
}) => {
  if (mixpanel) {
    mixpanel.peopleIncrement(property, value);
  }
};

/**
 * Record a transaction on the user profile, contributing to Mixpanel revenue
 * reports. Mirrors the legacy `mixpanel.people.track_charge` calls fired from
 * the donation/checkout controllers.
 */
const trackCharge = ({
  amount,
  properties,
  mixpanel,
}: {
  amount: number;
  properties?: Record<string, any>;
  mixpanel?: MixpanelHookMethods;
}) => {
  if (mixpanel) {
    mixpanel.peopleTrackCharge(amount, properties);
  }
};

/**
 * Register Mixpanel super properties so they ride along on every subsequent track call.
 */
const registerSuperProperties = ({
  properties,
  daysToPersist,
  mixpanel,
}: {
  properties: Record<string, any>;
  daysToPersist?: number;
  mixpanel?: MixpanelHookMethods;
}) => {
  if (mixpanel) {
    mixpanel.register(properties, daysToPersist);
  }
};

/**
 * Clear cookies + local storage when a user logs out.
 */
const reset = ({
  mixpanel,
  segment,
}: {
  mixpanel?: MixpanelHookMethods;
  segment?: SegmentHookMethods;
}) => {
  if (mixpanel) {
    mixpanel.reset();
  }

  if (segment) {
    segment.reset();
  }

  return;
};

export default {
  identify,
  track,
  reset,
  setPeopleProperties,
  setPeoplePropertyOnce,
  incrementPeopleProperty,
  trackCharge,
  registerSuperProperties,
};
